import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import {
  ALL_STATUS_IDS,
  PIPELINE_STATUS_IDS,
  MerchantStatusId,
  isMerchantStatusId,
} from '@/shared/merchantStatus';

// Status filter for the Admin › Merchants list (FIG-318 / REQ-011, REQ-017).
// The selection is remembered per admin in their own auth user_metadata, so it
// follows them to any device or browser they sign in from.

export const MERCHANT_STATUS_FILTER_METADATA_KEY = 'admin_merchants_status_filter';
const SAVE_DEBOUNCE_MS = 500;

function sameSelection(a: readonly MerchantStatusId[], b: readonly MerchantStatusId[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

/** Keeps known ids only, in display order. Returns null when nothing usable is stored. */
export function parseStoredStatusFilter(value: unknown): MerchantStatusId[] | null {
  if (!Array.isArray(value)) return null;
  const ids = new Set(value.filter(isMerchantStatusId));
  return ALL_STATUS_IDS.filter((id) => ids.has(id));
}

function toOrdered(ids: Iterable<MerchantStatusId>): MerchantStatusId[] {
  const set = new Set(ids);
  return ALL_STATUS_IDS.filter((id) => set.has(id));
}

export interface MerchantStatusFilter {
  selected: ReadonlySet<MerchantStatusId>;
  /** True when the selection differs from the default pipeline view. */
  isChanged: boolean;
  setSelected: (ids: Iterable<MerchantStatusId>) => void;
  toggle: (id: MerchantStatusId) => void;
  reset: () => void;
  selectAll: () => void;
}

export function useMerchantStatusFilter(): MerchantStatusFilter {
  const { user } = useAuth();
  const stored = parseStoredStatusFilter(user?.user_metadata?.[MERCHANT_STATUS_FILTER_METADATA_KEY]);

  const [selectedIds, setSelectedIds] = useState<MerchantStatusId[]>(() => stored ?? [...PIPELINE_STATUS_IDS]);
  const hydratedForUser = useRef<string | null>(stored ? user?.id ?? null : null);
  // The newest selection sent for saving (or already saved). Saves run one at
  // a time, in order, so the last one requested is the one remembered.
  const lastRequested = useRef<MerchantStatusId[] | null>(stored);
  const saveQueue = useRef<Promise<void>>(Promise.resolve());
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The auth user can arrive after the first render; apply their saved
  // selection once, without overriding changes made in this visit.
  useEffect(() => {
    if (!user?.id || hydratedForUser.current === user.id) return;
    hydratedForUser.current = user.id;
    if (stored) {
      lastRequested.current = stored;
      setSelectedIds(stored);
    }
    // `stored` is derived from `user`; re-running on user id is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const save = useCallback((ids: MerchantStatusId[]): Promise<void> => {
    if (lastRequested.current && sameSelection(lastRequested.current, ids)) return saveQueue.current;
    lastRequested.current = ids;
    saveQueue.current = saveQueue.current.then(async () => {
      try {
        const { error } = await supabase.auth.updateUser({
          data: { [MERCHANT_STATUS_FILTER_METADATA_KEY]: ids },
        });
        if (error) throw error;
      } catch (error) {
        // Let the same selection be retried on the next change.
        if (lastRequested.current === ids) lastRequested.current = null;
        // Remembering the filter is a convenience; the list keeps working.
        console.warn('Could not save the Merchants status filter', error);
      }
    });
    return saveQueue.current;
  }, []);

  const pendingSave = useRef<MerchantStatusId[] | null>(null);

  const persist = useCallback((ids: MerchantStatusId[]) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    pendingSave.current = ids;
    saveTimer.current = setTimeout(() => {
      pendingSave.current = null;
      void save(ids);
    }, SAVE_DEBOUNCE_MS);
  }, [save]);

  // Leaving the list (e.g. opening a merchant) must not drop a change made in
  // the last moment, so a pending save is sent straight away on unmount.
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (pendingSave.current) void save(pendingSave.current);
  }, [save]);

  const apply = useCallback((ids: Iterable<MerchantStatusId>) => {
    const next = toOrdered(ids);
    setSelectedIds(next);
    persist(next);
  }, [persist]);

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const toggle = useCallback((id: MerchantStatusId) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    apply(next);
  }, [apply, selectedIds]);

  return {
    selected,
    isChanged: !sameSelection(selectedIds, PIPELINE_STATUS_IDS),
    setSelected: apply,
    toggle,
    reset: () => apply(PIPELINE_STATUS_IDS),
    selectAll: () => apply(ALL_STATUS_IDS),
  };
}
